import React, { useEffect, useState } from "react";
import { ContainerPanel } from "../container-panel/container-panel";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { ContainerPane } from "../container-pane/container-pane";
import { useI18N } from "@src/language/language-context";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import {
  ZitadelServiceUser,
  UserManagementListActiveRoleEnum,
  UserManagementListActiveRoleRenderDataType,
} from "@src/types/user-management";
import { ServiceUsersList } from "../service-users-list/service-users-list";
import { useDispatch, useSelector } from "react-redux";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import Spinner from "@src/components/atoms/spinner/spinner";
import styles from "./service-users-list-view.module.scss";
import { getServiceUsersUseCase } from "@src/cloud-connect/user-manager/use-cases/get-service-users-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
import { UserManagementPageSearchBar } from "../user-management-page-search/user-management-page-searchbar";
const cssClassName = "service-users-list-view";

interface ServiceUsersListViewProps {
  serviceUsersListData?: ZitadelServiceUser[];
  userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType;
  onServiceUsersLoaded?: (serviceUsers: ZitadelServiceUser[]) => void;
  onRenderDataChange: (
    userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType,
  ) => void;
}

export const ServiceUsersListView = ({
  serviceUsersListData,
  userManagementListActiveRoleData,
  onServiceUsersLoaded,
  onRenderDataChange,
}: ServiceUsersListViewProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const isUserListResultsReady = useSelector(
    (state: RootState) => state.yourSlice.userListResultsReady,
  );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const [isLoading, setIsLoading] = useState(true);

  const texts = {
    subtitle: i18n("modules.serviceUsersListView.subtitle"),
    addNewServiceUserButton: i18n(
      "modules.serviceUsersListView.addNewServiceUserButton",
    ),
    errorSnackbarText: i18n("modules.serviceUsersListView.errorSnackbarText"),
    disabledAddNewUserButtonTooltip: i18n(
      "modules.serviceUsersListView.disabledAddNewUserButtonTooltip",
    ),
  };

  const handleAddNewServiceUser = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "addServiceUserDrawerModal",
      }),
    );
  };

  const loadServiceUsers = async (signal: AbortSignal) => {
    const result = await getServiceUsersUseCase(signal);

    if (signal?.aborted) return;

    if (result.success && result.data) {
      onServiceUsersLoaded?.(result.data);
    } else {
      console.error("error loading service users:", result.error);
      errorSnackbar(texts.errorSnackbarText, 5000);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsLoading(true);
    loadServiceUsers(signal).finally(() => {
      if (!signal?.aborted) setIsLoading(false);
    });
    return () => {
      controller.abort();
    };
  }, [isUserListResultsReady]);

  return (
    <ContainerPanel>
      <SubHeader>
        <FlexRowContainer justifyContent="space-between">
          <SubTitle text={texts.subtitle} />
          {authContext?.permissions?.includes("manage-service-api-users") && (
            <Button
              text={texts.addNewServiceUserButton}
              fun={handleAddNewServiceUser}
            />
          )}
        </FlexRowContainer>
      </SubHeader>
      <ContainerPane>
        <UserManagementPageSearchBar
          userManagementListActiveView={
            UserManagementListActiveRoleEnum.SERVICE_USERS
          }
          userManagementListActiveRoleData={userManagementListActiveRoleData}
          onRenderDataChange={onRenderDataChange}
        />
        {isLoading ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <Spinner />
          </div>
        ) : (
          <ServiceUsersList serviceUsers={serviceUsersListData} />
        )}
      </ContainerPane>
    </ContainerPanel>
  );
};
